import { useState, useEffect } from 'react';
import axios from 'axios';
import Switch from 'react-switch';
import Pagination from 'rc-pagination';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Cpu, Router, Search, X } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { getLanguage } from '../../util/languages';
import '../../design-system/index.css';
import './CpeCardsView.scss';
import { LoadingSpinner } from '../loading-spinner/LoadingSpinner';

// Define TypeScript interfaces for the CPE data
interface CpeDevice {
  ID: number;
  registrant: string;
  dateRegistered: string;
  dateVerified: string;
  accountId: number;
  serial: string;
  firmwareVersion: string;
  lastBootUTC: string;
  categories: string;
}

interface CpeApiResponse {
  headers: Array<{
    key: string;
    sortable: boolean;
  }>;
  data: CpeDevice[];
  records: number;
  size: number;
  start: number;
  end: number;
  page: number;
  pages: number;
  allDnsRequests: Array<{
    allRequests?: number;
    allUniqueUrlsVisited?: number;
    allBlockedRequests?: number;
    allUniqueUrlsBlocked?: number;
  }>;
  sites: Array<{
    top3Visited: Array<{
      count: number | null;
      reason: string | null;
      categories: string;
    }>;
    top3Blocked: any[];
  }>;
  allCategories: Array<{
    catagoryName: string;
  }>;
}

interface CpeCardsViewProps {
  operatorId: string | number;
  darkMode: boolean;
}

export const CpeCardsView: React.FC<CpeCardsViewProps> = ({ operatorId, darkMode }) => {
  const { t, i18n } = useTranslation();
  const dateLocale = getLanguage(i18n.language).locale;
  const [cpeData, setCpeData] = useState<CpeDevice[]>([]);
  const [allData, setAllData] = useState<CpeDevice[]>([]);
  const [filteredData, setFilteredData] = useState<CpeDevice[]>([]);
  const [displayData, setDisplayData] = useState<CpeDevice[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const navigate = useNavigate();
  
  // Enhanced state for incremental loading
  const [loadingState, setLoadingState] = useState({
    currentSize: 500, // Start with pageSize
    totalRecords: 0,
    currentFetchedCount: 0
  });

  const [pagination, setPagination] = useState({
    page: 1,
    size: 3, // Maximum 3 cards per page
    total: 0,
  });

  // Fetch CPE data with incremental loading
  useEffect(() => {
    if (operatorId) {
      fetchCpeData();
    }
  }, [operatorId]);

  // NEW: Function to fetch ALL CPE data with incremental loading
  const fetchCpeData = async () => {
    if (!operatorId) return;

    setIsLoading(true);
    try {
      let allCpeDevices: CpeDevice[] = [];
      let currentPage = 1;
      const pageSize = 500;
      let hasMoreData = true;
      let totalRecords = 0;

      // Fetch data incrementally
      while (hasMoreData) {
        const response = await axios.get<CpeApiResponse>(`/op/${operatorId}/cpe_table`, {
          params: { 
            page: currentPage, 
            size: loadingState.currentSize 
          }
        });

        const result = response.data;
        const cpeDevices: CpeDevice[] = result.data || [];
        
        if (cpeDevices.length > 0) {
          allCpeDevices = [...allCpeDevices, ...cpeDevices];
          totalRecords = result.records  || allCpeDevices.length;
          
          // Update loading state with current fetch count
          setLoadingState(prev => ({
            ...prev,
            currentFetchedCount: allCpeDevices.length,
            totalRecords: totalRecords
          }));

          // Check if we need to fetch more data incrementally
          if (allCpeDevices.length === loadingState.currentSize && allCpeDevices.length < totalRecords) {
            // Increment the page size for next fetch
            setLoadingState(prev => ({ 
              ...prev, 
              currentSize: prev.currentSize + pageSize 
            }));
            currentPage++;
          } else {
            hasMoreData = false;
          }
        } else {
          hasMoreData = false;
        }

        // Safety check to prevent infinite loops
        if (currentPage > 100) {
          console.warn('Safety break: Too many pages fetched');
          hasMoreData = false;
        }
      }

      setAllData(allCpeDevices);
      setFilteredData(allCpeDevices);

      setPagination(prev => ({
        ...prev,
        page: 1,
        total: allCpeDevices.length
      }));

      console.log(`Fetched ${allCpeDevices.length} CPE accounts total out of ${totalRecords} records`);

    } catch (error) {
      console.error('Error fetching CPE data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Alternative approach: Single fetch with incremental loading detection
  const fetchCpeDataAlternative = async () => {
    if (!operatorId) return;

    setIsLoading(true);
    try {
      const response = await axios.get<CpeApiResponse>(`/op/${operatorId}/cpe_table`, {
        params: { 
          page: 1, 
          size: loadingState.currentSize 
        }
      });

      const result = response.data;
      const cpeDevices: CpeDevice[] = result.data || [];
      const totalRecords = result.records|| 0;

      setAllData(cpeDevices);
      setFilteredData(cpeDevices);

      // Update loading state
      setLoadingState(prev => ({
        ...prev,
        currentFetchedCount: cpeDevices.length,
        totalRecords: totalRecords
      }));

      // Implement the incremental loading logic
      if (cpeDevices.length === loadingState.currentSize && cpeDevices.length < totalRecords) {
        // Increment size for next potential fetch
        setLoadingState(prev => ({ 
          ...prev, 
          currentSize: prev.currentSize + 500 // Increase by pageSize
        }));
        
        console.log(`More CPE data available. Current: ${cpeDevices.length}, Total: ${totalRecords}`);
      }

      setPagination(prev => ({
        ...prev,
        page: 1,
        total: cpeDevices.length
      }));

      console.log(`Fetched ${cpeDevices.length} CPE accounts`);

    } catch (error) {
      console.error('Error fetching CPE data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load more data function for manual triggering
  const loadMoreCpeData = async () => {
    if (!operatorId || isLoading) return;

    setIsLoading(true);
    try {
      const nextPage = Math.floor(loadingState.currentFetchedCount / 500) + 1;
      
      const response = await axios.get<CpeApiResponse>(`/op/${operatorId}/cpe_table`, {
        params: { 
          page: nextPage, 
          size: 500 
        }
      });

      const result = response.data;
      const newData: CpeDevice[] = result.data || [];

      if (newData.length > 0) {
        const updatedData = [...allData, ...newData];
        
        setAllData(updatedData);
        setFilteredData(updatedData);

        setLoadingState(prev => ({
          ...prev,
          currentFetchedCount: updatedData.length,
          currentSize: prev.currentSize + 500
        }));

        setPagination(prev => ({
          ...prev,
          total: updatedData.length
        }));

        console.log(`Loaded ${newData.length} more CPE accounts. Total: ${updatedData.length}`);
      }

    } catch (error) {
      console.error('Error loading more CPE data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to trigger incremental loading when currentSize changes
  useEffect(() => {
    if (loadingState.currentSize > 500 && loadingState.currentFetchedCount < loadingState.totalRecords) {
      fetchCpeData();
    }
  }, [loadingState.currentSize]);

  // Filter data based on search
  useEffect(() => {
    let filtered = allData;

    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (cpe) =>
          cpe.registrant?.toLowerCase().includes(query) ||
          cpe.serial?.toLowerCase().includes(query) ||
          cpe.firmwareVersion?.toLowerCase().includes(query) ||
          cpe.categories?.toLowerCase().includes(query) ||
          cpe.ID?.toString().includes(query)
      );
    }

    setFilteredData(filtered);
    setPagination(prev => ({
      ...prev,
      page: 1, // Reset to first page when filters change
      total: filtered.length
    }));
  }, [searchQuery, allData]);

  // Paginate filtered data - Only show 3 cards per page
  useEffect(() => {
    const startIndex = (pagination.page - 1) * pagination.size;
    const endIndex = startIndex + pagination.size;
    const paginatedResults = filteredData.slice(startIndex, endIndex);
    setDisplayData(paginatedResults);
  }, [filteredData, pagination.page, pagination.size]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination(prev => ({
      ...prev,
      page,
      size: pageSize || prev.size
    }));
  };

  function goToCpePage(cpeID: any) {
    navigate(`/operators/${operatorId}/cpe/${cpeID}`);
  }

  // Card date: day on one line, UTC time muted underneath, full timestamp on hover
  function renderCardDate(dateString?: string) {
    const date = dateString && typeof dateString === 'string' ? new Date(dateString) : null;
    if (!date || isNaN(date.getTime())) {
      return <span className="cpe-card-date empty">—</span>;
    }

    const day = date.toLocaleDateString(dateLocale, { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
    const time = date.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' });
    return (
      <time className="cpe-card-date" dateTime={date.toISOString()} title={`${day}, ${time} UTC`}>
        <span className="cpe-card-date-day">{day}</span>
        <span className="cpe-card-date-time">{time} UTC</span>
      </time>
    );
  }

  // Status tone (design-system chip variant) based on verification
  const getStatusTone = (dateRegistered: string, dateVerified: string | null | undefined): string => {
    if (!dateVerified) return 'warn';
    try {
      const registered = new Date(dateRegistered);
      const verified = new Date(dateVerified);
      return verified >= registered ? 'mint' : 'danger';
    } catch (error) {
      return '';
    }
  };

  // Firmware version tone
  const getFirmwareTone = (firmware: string | null | undefined): string => {
    if (!firmware) return '';
    if (firmware.includes('2.4.3')) return 'mint';
    if (firmware.includes('2.4.2')) return 'mint';
    if (firmware.includes('2.4.1')) return 'warn';
    return 'violet';
  };

  // Get status text
  const getStatusText = (dateVerified: string | null | undefined): string => {
    if (!dateVerified) return t('cpeCards.status.pending');
    return t('cpeCards.status.verified');
  };

  const avatarColors = ['', 'blue', 'violet', 'amber', 'slate'];
  const moreAvailable = loadingState.totalRecords - loadingState.currentFetchedCount;
  const totalPages = Math.ceil(filteredData.length / pagination.size);

  return (
    <div className="bd cpe-cards-view w-full" data-theme={darkMode ? 'dark' : 'light'} data-accent="mint">
      <div className="bd-card cpe-cards-container">
        <header className="cpe-cards-hd">
          <div className="cpe-cards-title">
            <h2 className="bd-h3">{t('cpeCards.title')}</h2>
            <span className="bd-meta">{t('cpeCards.subtitle')}</span>
            {isLoading && (
              <span className="bd-meta cpe-cards-loading">
                <span className="cpe-spinner" />
                {t('cpeCards.loading.data')} {loadingState.currentFetchedCount > 0 &&
                  t('cpeCards.loading.progress', { loaded: loadingState.currentFetchedCount, total: loadingState.totalRecords })}
              </span>
            )}
          </div>

          {/* 🔍 Filters Section */}
          <div className="cpe-cards-tools">
            <label className="bd-search">
              <Search size={15} />
              <input
                type="text"
                placeholder={t('cpeCards.search.placeholder')}
                aria-label={t('cpeCards.search.ariaLabel')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setSearchQuery(''); }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="bd-search-clear"
                  aria-label={t('cpeCards.search.clear')}
                  onClick={(e) => {
                    setSearchQuery('');
                    e.currentTarget.parentElement?.querySelector('input')?.focus();
                  }}
                >
                  <X size={12} strokeWidth={2.5} />
                </button>
              )}
            </label>
            <span className="bd-chip">
              <span className="bd-mono">{filteredData.length}</span> {t('cpeCards.count.cpes')}
              {moreAvailable > 0 && <span className="cpe-warn-text">&nbsp;{t('cpeCards.count.ofTotal', { total: loadingState.totalRecords })}</span>}
            </span>
          </div>
        </header>

        {/* Results Summary */}
        {filteredData.length > 0 && (
          <div className="cpe-cards-summary bd-meta">
            <Trans
              t={t}
              i18nKey="cpeCards.summary.showing"
              values={{ shown: displayData.length, total: filteredData.length }}
              components={{ mono: <span className="bd-mono" /> }}
            />
            {filteredData.length !== allData.length && ` ${t('cpeCards.summary.filteredFrom', { total: allData.length })}`}
            {moreAvailable > 0 && <span className="cpe-warn-text"> · {t('cpeCards.summary.moreAvailable', { number: moreAvailable })}</span>}
          </div>
        )}

        {/* Cards Grid - Maximum 3 cards per page */}
        <div className="cpe-cards-body">
          {isLoading && allData.length === 0 ? (
            <LoadingSpinner text={t('cpeCards.empty.loading')} />
          ) : displayData.length === 0 ? (
            <div className="cpe-cards-empty">
              {allData.length === 0 ? t('cpeCards.empty.none') : t('cpeCards.empty.noMatch')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayData.map((cpe) => (
                <div
                  key={cpe.ID}
                  className="cpe-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => { goToCpePage(cpe.ID); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') goToCpePage(cpe.ID); }}
                >
                  {/* Header - Registrant as Heading */}
                  <div className="cpe-card-hd">
                    <span className={`bd-avatar ${avatarColors[cpe.ID % avatarColors.length]}`}>
                      {(cpe.registrant || '?').charAt(0).toUpperCase()}
                    </span>
                    <div className="cpe-card-id">
                      <h3 className="cpe-card-name" title={cpe.registrant}>{cpe.registrant}</h3>
                      <span className="bd-meta">
                        <Trans t={t} i18nKey="cpeCards.card.account" values={{ id: cpe.ID }} components={{ mono: <span className="bd-mono" /> }} />
                      </span>
                    </div>
                    <span className={`bd-chip ${getStatusTone(cpe.dateRegistered, cpe.dateVerified)}`}>
                      <span className="dot" />
                      {getStatusText(cpe.dateVerified)}
                    </span>
                  </div>

                  {/* Main Content */}
                  <div className="cpe-card-rows">
                    <div className="cpe-card-row">
                      <span className="cpe-card-label"><Router size={14} /> {t('cpeCards.card.serialNumber')}</span>
                      <span className="bd-tag">{cpe.serial || t('cpeCards.card.notAvailable')}</span>
                    </div>
                    <div className="cpe-card-row">
                      <span className="cpe-card-label"><Cpu size={14} /> {t('cpeCards.card.firmware')}</span>
                      <span className={`bd-chip bd-mono ${getFirmwareTone(cpe.firmwareVersion)}`}>
                        {cpe.firmwareVersion || t('cpeCards.card.notAvailable')}
                      </span>
                    </div>
                  </div>

                  {/* Dates Section */}
                  <div className="cpe-card-dates">
                    <div>
                      <span className="bd-meta">{t('cpeCards.card.registered')}</span>
                      {renderCardDate(cpe.dateRegistered)}
                    </div>
                    <div>
                      <span className="bd-meta">{t('cpeCards.card.verified')}</span>
                      {renderCardDate(cpe.dateVerified)}
                    </div>
                    <div>
                      <span className="bd-meta">{t('cpeCards.card.lastBoot')}</span>
                      {renderCardDate(cpe.lastBootUTC)}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="cpe-card-ft">
                    {t('cpeCards.card.viewDetails')}
                    <ArrowRight size={14} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load More and Pagination Section */}
        {filteredData.length > 0 && (
          <div className="cpe-cards-ft">
            <span className="bd-meta">
              <Trans
                t={t}
                i18nKey="cpeCards.pagination.pageOf"
                values={{ page: pagination.page, pages: totalPages }}
                components={{ mono: <span className="bd-mono" /> }}
              />
              {moreAvailable > 0 && <span className="cpe-warn-text"> ({t('cpeCards.summary.moreAvailable', { number: moreAvailable })})</span>}
            </span>

            <div className="flex items-center gap-4">
              {/* Load More Button */}
              {moreAvailable > 0 && (
                <button onClick={loadMoreCpeData} disabled={isLoading} className="bd-pill accent cpe-load-more">
                  {isLoading ? t('cpeCards.pagination.loading') : t('cpeCards.pagination.loadMore', { number: moreAvailable })}
                </button>
              )}

              {/* Regular Pagination - Only show if there are more than 3 accounts */}
              {filteredData.length > 3 && (
                <Pagination
                  className="pagination bd-pagination"
                  current={pagination.page}
                  pageSize={pagination.size}
                  total={filteredData.length}
                  onChange={handlePageChange}
                  nextIcon={<ChevronRight size={16} />}
                  prevIcon={<ChevronLeft size={16} />}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
